import { useState, useEffect, useCallback, useRef } from "react";
import { nomesService } from "../../../services/api";
import { useDebounce } from "../../../hooks/useDebounce";

/**
 * Hook personalizado para gerenciar a lógica de negócios relacionada aos nomes
 */
export const useNomes = () => {
  // Estados para lista de nomes e carregamento
  const [nomes, setNomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("ATIVO");
  const [nameFilter, setNameFilter] = useState("");
  
  // Estados para paginação
  const paginaAtual = useRef(1);
  const [limit] = useState(100);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  
  // Ref para armazenar os filtros atuais
  const filtrosAtuais = useRef({
    status: selectedStatus,
    nome: ""
  });
  
  // Flag para controlar se estamos carregando mais ou fazendo uma nova busca
  const carregandoMais = useRef(false);
  
  // Aplicar debounce ao filtro de nome (1 segundo)
  const debouncedNameFilter = useDebounce(nameFilter, 1000);

  // Estados para seleção em lote
  const [selectedItems, setSelectedItems] = useState([]);
  const [confirmBatchDelete, setConfirmBatchDelete] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({
    isDeleting: false,
    total: 0,
    completed: 0,
    success: 0,
    failed: 0,
  });

  // Buscar nomes com base no status selecionado e filtro de nome
  const fetchNomes = useCallback(async (status = selectedStatus, nome = debouncedNameFilter, resetPagination = true) => {
    // Se estamos carregando mais, não executamos uma nova busca completa
    if (carregandoMais.current) {
      console.log("Ignorando fetchNomes durante carregamento de mais registros");
      return;
    }
    
    try {
      setLoading(true);
      
      // Atualizar filtros atuais
      filtrosAtuais.current = {
        status,
        nome
      };
      
      // Se estamos resetando a paginação (novo filtro), começamos da página 1
      if (resetPagination) {
        paginaAtual.current = 1;
        setNomes([]);
      }
      
      // Calcular o skip com base na página atual
      const skip = resetPagination ? 0 : (paginaAtual.current - 1) * limit;
      
      console.log(`Buscando dados: página=${paginaAtual.current}, skip=${skip}, limit=${limit}, resetPagination=${resetPagination}`);
      
      // Buscar dados paginados
      const data = await nomesService.buscarNomes(status, nome, skip, limit);
      
      // Se estamos carregando a primeira página ou resetando, substituímos os dados
      // Se estamos carregando mais, anexamos aos dados existentes
      if (resetPagination) {
        setNomes(data);
      } else {
        setNomes(prev => [...prev, ...data]);
      }
      
      try {
        // Buscar total para verificar se há mais registros
        const totalData = await nomesService.buscarTotalNomes(status, nome);
        
        // Verificar diferentes formatos possíveis da resposta
        let totalCount = 0;
        if (totalData && typeof totalData === 'object') {
          if (totalData.count !== undefined) {
            totalCount = totalData.count;
          } else if (totalData.total !== undefined) {
            totalCount = totalData.total;
          } else {
            // Se não encontrarmos um campo específico, usamos o comprimento do array como fallback
            totalCount = Math.max(data.length, skip + data.length);
          }
        } else if (typeof totalData === 'number') {
          totalCount = totalData;
        } else {
          // Fallback: usar o tamanho atual + 100 se houver dados
          totalCount = data.length > 0 ? Math.max(data.length, skip + data.length) : 0;
        }
        
        setTotal(totalCount);
        
        // Verificar se há mais dados para carregar
        setHasMore(skip + data.length < totalCount);
      } catch (error) {
        console.error("Erro ao buscar contagem total:", error);
        // Fallback: usar o tamanho atual dos dados
        setTotal(Math.max(data.length, skip + data.length));
        setHasMore(data.length >= limit); // Se retornou o limite completo, assume que pode haver mais
      }
      
      setLoading(false);
      
      // Limpar seleções quando os dados são atualizados com novos filtros
      if (resetPagination) {
        setSelectedItems([]);
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      setError(error.message);
      setLoading(false);
    }
  }, [selectedStatus, debouncedNameFilter, limit]);

  // Carregar mais registros
  const loadMore = async () => {
    if (hasMore && !loadingMore) {
      try {
        // Marcar que estamos carregando mais para evitar chamadas duplicadas
        carregandoMais.current = true;
        setLoadingMore(true);
        
        // Incrementar a página atual
        paginaAtual.current = paginaAtual.current + 1;
        
        // Calcular o skip com base na nova página
        const skip = (paginaAtual.current - 1) * limit;
        
        console.log(`Carregando mais: página=${paginaAtual.current}, skip=${skip}, limit=${limit}`);
        
        // Buscar próxima página usando os filtros atuais
        const data = await nomesService.buscarNomes(
          filtrosAtuais.current.status, 
          filtrosAtuais.current.nome, 
          skip, 
          limit
        );
        
        console.log(`Registros adicionais carregados: ${data.length}`);
        
        // Anexar novos dados aos existentes
        setNomes(prevNomes => [...prevNomes, ...data]);
        
        // Verificar se ainda há mais dados
        setHasMore(skip + data.length < total);
        
        setLoadingMore(false);
        // Desmarcar que estamos carregando mais
        carregandoMais.current = false;
      } catch (error) {
        console.error("Erro ao carregar mais dados:", error);
        setLoadingMore(false);
        carregandoMais.current = false;
      }
    }
  };

  // Buscar opções de status disponíveis
  const fetchStatusOptions = async () => {
    try {
      const data = await nomesService.buscarStatusNomes();
      setStatusOptions(Object.entries(data).map(([key, value]) => ({ key, value })));
    } catch (error) {
      console.error("Erro ao buscar status:", error);
    }
  };

  // Efeito para buscar dados quando o filtro debounceado mudar
  useEffect(() => {
    // Se não estamos carregando mais, então fazemos uma nova busca
    if (!carregandoMais.current) {
      console.log("Filtro mudou, reiniciando paginação");
      fetchNomes(selectedStatus, debouncedNameFilter, true);
    }
  }, [debouncedNameFilter, selectedStatus, fetchNomes]);

  // Carregar dados iniciais
  useEffect(() => {
    fetchStatusOptions();
  }, []);

  // Alterar status selecionado
  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    setSelectedStatus(newStatus);
  };

  // Alterar filtro de nome
  const handleNameFilterChange = (e) => {
    const newNameFilter = e.target.value;
    setNameFilter(newNameFilter);
  };

  // Excluir nome individual
  const excluirNome = async (id) => {
    try {
      await nomesService.cancelarNome(id);
      // Atualiza a lista após excluir
      fetchNomes(selectedStatus, debouncedNameFilter, true);
      return true;
    } catch (error) {
      console.error("Erro ao excluir nome:", error);
      return false;
    }
  };

  // Funções para seleção em lote
  const handleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === nomes.length) {
      // Se todos já estão selecionados, desmarca todos
      setSelectedItems([]);
    } else {
      // Seleciona todos
      setSelectedItems(nomes.map(item => item.id));
    }
  };

  // Exclusão em lote
  const iniciarExclusaoEmLote = () => {
    if (selectedItems.length > 0) {
      setConfirmBatchDelete(true);
    }
  };

  const cancelarExclusaoEmLote = () => {
    setConfirmBatchDelete(false);
    // Reseta o progresso se estiver em andamento
    if (batchDeleteProgress.isDeleting) {
      setBatchDeleteProgress({
        isDeleting: false,
        total: 0,
        completed: 0,
        success: 0,
        failed: 0
      });
    }
  };

  const confirmarExclusaoEmLote = async () => {
    try {
      // Inicializa o progresso
      setBatchDeleteProgress({
        isDeleting: true,
        total: selectedItems.length,
        completed: 0,
        success: 0,
        failed: 0
      });

      // Executa as exclusões em sequência
      for (let i = 0; i < selectedItems.length; i++) {
        const id = selectedItems[i];
        try {
          await nomesService.cancelarNome(id);
          // Atualiza o progresso de sucesso
          setBatchDeleteProgress(prev => ({
            ...prev,
            completed: i + 1,
            success: prev.success + 1
          }));
        } catch (error) {
          console.error(`Erro ao excluir nome ID ${id}:`, error);
          // Atualiza o progresso de falha
          setBatchDeleteProgress(prev => ({
            ...prev,
            completed: i + 1,
            failed: prev.failed + 1
          }));
        }
        
        // Pequena pausa para permitir a atualização da UI
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Finaliza o processo após todas as exclusões
      setTimeout(() => {
        // Atualiza a lista após excluir todos
        fetchNomes(selectedStatus, debouncedNameFilter, true);
        setConfirmBatchDelete(false);
        setSelectedItems([]);
        setBatchDeleteProgress({
          isDeleting: false,
          total: 0,
          completed: 0,
          success: 0,
          failed: 0
        });
      }, 500);
    } catch (error) {
      console.error("Erro ao excluir nomes em lote:", error);
      setBatchDeleteProgress(prev => ({
        ...prev,
        isDeleting: false
      }));
    }
  };

  return {
    // Dados
    nomes,
    loading,
    loadingMore,
    error,
    statusOptions,
    selectedStatus,
    nameFilter,
    selectedItems,
    confirmBatchDelete,
    batchDeleteProgress,
    hasMore,
    total,
    
    // Ações
    fetchNomes,
    loadMore,
    handleStatusChange,
    handleNameFilterChange,
    excluirNome,
    handleSelectItem,
    handleSelectAll,
    iniciarExclusaoEmLote,
    cancelarExclusaoEmLote,
    confirmarExclusaoEmLote,
  };
};
